import { useMemo, useState } from "react";
import { Button } from "../../../components/ui/button";
import { BookOpen, Shield, Wrench, Edit, Trash2, ChevronDown, ChevronUp, ChevronRight } from "lucide-react";
import InsurancesDialog from "../InsurancesDialog";
import MaintenanceDialog from "../MaintenanceDialog";
import LogbookDialog from "./LogbookDialog";
import MaintenanceTimeline from "./MaintenanceTimeline";
import { HealthBadge } from "../../../components/HealthBadge";
import { buildTimeline } from "../../../lib/aircraftTimeline";
import { DocChip, DOC_FIELDS } from "./AircraftForm";

function fmtMin(m) {
  if (m == null) return "—";
  const sign = m < 0 ? "-" : "";
  const v = Math.abs(m);
  return `${sign}${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
}

const KIND_STYLE = {
  grounded: { card: "border-red-500/30 bg-red-500/5", chip: "bg-red-500/15 text-red-300 border-red-500/30" },
  warn: { card: "border-amber-500/30 bg-amber-500/5", chip: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  ok: { card: "border-emerald-500/30 bg-emerald-500/5", chip: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  retired: { card: "border-slate-600/50 bg-slate-800/30", chip: "bg-slate-700/60 text-slate-300 border-slate-600/50" },
};

const GROUPS = [
  { key: "blocks", label: "Não deixa voar", text: "text-red-300" },
  { key: "attention", label: "Atenção", text: "text-amber-300" },
  { key: "later", label: "Mais tarde", text: "text-blue-300" },
];

// Cada linha da lista é clicável: abre o sítio onde se resolve (o texto aparece ao passar o rato)
const GO_LABEL = {
  documents: "Abrir documentos",
  maintenance: "Abrir manutenções",
  insurance: "Abrir seguros",
  logbook: "Abrir caderneta",
};

const DOT = { critical: "bg-red-500", warn: "bg-amber-400", info: "bg-blue-400" };

function IssueRow({ row, onGo }) {
  return (
    <button
      type="button"
      onClick={() => onGo(row.target)}
      title={GO_LABEL[row.target] || "Abrir"}
      className="w-full text-left flex items-start gap-2.5 px-3 py-2 border-t border-slate-700/60 hover:bg-slate-800/60 focus:outline-none focus-visible:bg-slate-800/60 transition-colors"
      data-testid="di-ac-issue"
    >
      <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${DOT[row.severity] || DOT.info}`} />
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-medium text-slate-100">{row.title}</div>
        {row.when && <div className="text-[11px] text-slate-400 leading-snug">{row.when}</div>}
      </div>
      <ChevronRight className="w-3.5 h-3.5 text-slate-500 shrink-0 mt-1" aria-hidden="true" />
    </button>
  );
}

export default function AircraftCard({ aircraft, extras, view, health, expanded, onToggleExpand, onEdit, onDelete, onChanged }) {
  const [showLog, setShowLog] = useState(false);
  const [showIns, setShowIns] = useState(false);
  const [showMaint, setShowMaint] = useState(false);
  const st = aircraft.operational_state || {};
  const docs = st.criteria?.documents || {};
  const { gapInfo, insurance: activeInsurance, maintItems, perDayMin } = extras || {};
  const covered = view?.covered;
  const timeline = useMemo(
    () => buildTimeline({ aircraft, items: maintItems, insurance: activeInsurance, perDayMin, covered }),
    [aircraft, maintItems, activeInsurance, perDayMin, covered],
  );
  const style = view ? KIND_STYLE[view.kind] : { card: "border-slate-700 bg-slate-800/20", chip: "bg-slate-700/60 text-slate-300 border-slate-600/50" };

  const totalMissing = gapInfo ? (gapInfo.service_numbers?.missing_count || 0) + (gapInfo.navigation_log_numbers?.missing_count || 0) : 0;

  const insDaysRemaining = (() => {
    if (!activeInsurance?.policy_end) return null;
    const end = new Date(activeInsurance.policy_end + "T00:00:00");
    const now = new Date(); now.setHours(0, 0, 0, 0);
    return Math.round((end - now) / 86400000);
  })();
  // Vermelho só depois de vencido (como os outros documentos); nos últimos 30 dias fica âmbar
  const insOk = insDaysRemaining != null && insDaysRemaining >= 0;
  const insWarn = insOk && insDaysRemaining < 30;
  const insMissing = !activeInsurance;

  // Ao fechar uma janela (caderneta, seguros, manutenções) o estado pode ter mudado: atualiza o ecrã
  const closeDialog = (setter) => () => { setter(false); onChanged?.(); };

  const go = (target) => {
    if (target === "maintenance") setShowMaint(true);
    else if (target === "insurance") setShowIns(true);
    else if (target === "logbook") setShowLog(true);
    else onEdit?.();
  };

  return (
    <div className={`p-3 rounded border transition-all ${style.card} ${expanded ? "ring-2 ring-blue-500/30" : ""}`} data-testid={`di-ac-card-${aircraft.id}`}>
      <div className="flex items-start gap-2">
        <button onClick={onToggleExpand} className="flex-1 min-w-0 text-left" data-testid="di-ac-toggle-expand" aria-expanded={expanded}>
          <div className="flex items-center gap-2 flex-wrap">
            {health && <HealthBadge score={health.score} tier={health.tier} factors={health.factors} compact />}
            <h4 className="font-bold text-slate-100 text-sm" data-testid="di-ac-registration">{aircraft.registration}</h4>
            <span className="text-[11px] text-slate-500">{aircraft.model} · {fmtMin(aircraft.current_hours_min)} no motor</span>
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-semibold ${style.chip}`} data-testid="di-ac-state">
              {view ? view.phrase : "A calcular…"}
            </span>
            {view && <span className="text-[11px] text-slate-400 min-w-0" data-testid="di-ac-reason">{view.reason}</span>}
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => setShowLog(true)} className="relative p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-blue-300" title={totalMissing ? `Caderneta · ${totalMissing} registo(s) em falta` : "Caderneta"} data-testid="di-ac-logbook-btn">
            <BookOpen className="w-3.5 h-3.5" />
            {totalMissing > 0 && <span className="absolute -top-1 -right-1 bg-amber-500 text-slate-900 text-[9px] font-bold rounded-full px-1 leading-tight min-w-[14px] text-center" data-testid="di-ac-logbook-gap-badge">{totalMissing > 9 ? "9+" : totalMissing}</span>}
          </button>
          <button onClick={() => setShowIns(true)} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-emerald-300" title="Seguros" data-testid="di-ac-insurance-btn"><Shield className="w-3.5 h-3.5" /></button>
          <button onClick={() => setShowMaint(true)} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-amber-300" title="Manutenções" data-testid="di-ac-maintenance-btn"><Wrench className="w-3.5 h-3.5" /></button>
          <button onClick={onEdit} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-orange-300" title="Editar" data-testid="di-ac-edit-btn"><Edit className="w-3.5 h-3.5" /></button>
          <button onClick={onDelete} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-red-400" title="Eliminar" data-testid="di-ac-delete-btn"><Trash2 className="w-3.5 h-3.5" /></button>
          <button onClick={onToggleExpand} className="p-1 text-slate-500" aria-label={expanded ? "Fechar" : "Abrir"}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-700 space-y-3" data-testid="di-ac-drawer">
          {aircraft.status === "retirada" ? (
            <div className="text-[11px] px-2 py-1 rounded bg-slate-700/60 text-slate-400 border border-slate-600/50 flex items-center gap-1.5" data-testid="di-ac-status">
              <span>⚓</span> Retirada de serviço
            </div>
          ) : (
            <div className="rounded border border-slate-700/60 overflow-hidden" data-testid="di-ac-issues">
              {view && GROUPS.map((g) => (view[g.key].length > 0 && (
                <div key={g.key}>
                  <div className={`px-3 pt-2 pb-1 text-[11px] font-semibold ${g.text} border-t border-slate-700/60 first:border-t-0`}>
                    {g.label} ({view[g.key].length})
                  </div>
                  {view[g.key].map((row) => <IssueRow key={row.key} row={row} onGo={go} />)}
                </div>
              )))}
              {view && view.blocks.length + view.attention.length + view.later.length === 0 && (
                <div className="px-3 py-2 text-[11px] text-emerald-300/80 italic" data-testid="di-ac-no-issues">Tudo em dia. Sem avisos.</div>
              )}
              {!view && <div className="px-3 py-2 text-[11px] text-slate-500">A calcular o estado…</div>}
            </div>
          )}
          {aircraft.status !== "retirada" && (
            <div data-testid="di-ac-timeline">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1 font-semibold flex items-center gap-1">📅 Próximos 90 dias</div>
              <MaintenanceTimeline timeline={timeline} onGo={go} />
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5 font-semibold flex items-center gap-1">📋 Documentos</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-[11px]">
              {DOC_FIELDS.map((f) => { const d = docs[f.key] || {}; return <DocChip key={f.key} label={f.label} icon={f.icon} ok={d.ok} valid={d.valid_until} days={d.days_remaining} missing={d.missing} />; })}
              <DocChip label="Seguro" ok={insOk} warn={insWarn} valid={activeInsurance?.policy_end || null} days={insDaysRemaining} missing={insMissing} />
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 pt-1">
            <Button size="sm" variant="outline" className="text-[11px] border-slate-700" onClick={() => setShowLog(true)} data-testid="di-ac-drawer-logbook"><BookOpen className="w-3.5 h-3.5 mr-1" /> Caderneta</Button>
            <Button size="sm" variant="outline" className="text-[11px] border-slate-700" onClick={() => setShowMaint(true)} data-testid="di-ac-drawer-maint"><Wrench className="w-3.5 h-3.5 mr-1" /> Manutenções</Button>
            <Button size="sm" variant="outline" className="text-[11px] border-slate-700" onClick={() => setShowIns(true)} data-testid="di-ac-drawer-ins"><Shield className="w-3.5 h-3.5 mr-1" /> Seguros</Button>
            <Button size="sm" variant="outline" className="text-[11px] border-slate-700" onClick={onEdit} data-testid="di-ac-drawer-edit"><Edit className="w-3.5 h-3.5 mr-1" /> Editar / Documentos</Button>
          </div>
          {aircraft.notes && <div className="text-[10px] text-slate-400 italic whitespace-pre-wrap">{aircraft.notes}</div>}
        </div>
      )}

      {showLog && <LogbookDialog aircraftId={aircraft.id} registration={aircraft.registration} onClose={closeDialog(setShowLog)} />}
      {showIns && <InsurancesDialog aircraftId={aircraft.id} registration={aircraft.registration} onClose={closeDialog(setShowIns)} />}
      {showMaint && <MaintenanceDialog aircraftId={aircraft.id} registration={aircraft.registration} onClose={closeDialog(setShowMaint)} />}
    </div>
  );
}
